import React, {useMemo, useState} from "react";
import {Modal} from "../../../../../components/modal/modal.jsx";
import Button from "../../../../../components/button/button.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import {useQuery} from "@tanstack/react-query";
import {FaEraser, FaSearch} from "react-icons/fa";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../../../utils/ag-grid-columns-utils.js";
import ActivityService from "../../../../../services/dispatch/activity-service.js";
import {activityLinkUrl, brDateToIso} from "../utils/activity-utils.js";
import ActivityLink from "./activity-link.jsx";

const initialFilters = {leader: "", name: ""};

const ActivityGarrisonModal = ({open, setOpen, dateOnDuty, selectedIds = [], inspectorNameWar, onConfirm}) => {
    const [gridApi, setGridApi] = useState(null);
    const [filterValues, setFilterValues] = useState(initialFilters);
    const [filters, setFilters] = useState(initialFilters);

    const isoDate = brDateToIso(dateOnDuty);

    const {data: rows = []} = useQuery({
        queryFn: () => ActivityService.garrisonsByDate(isoDate),
        queryKey: ["activity-garrisons-by-date", isoDate],
        enabled: open && !!isoDate,
    });

    const filteredRows = useMemo(() => {
        const leader = filters.leader.trim().toLowerCase();
        const name = filters.name.trim().toLowerCase();
        return (rows ?? []).filter(row =>
            (!leader || (row.leader ?? "").toLowerCase().includes(leader)) &&
            (!name || (row.name ?? "").toLowerCase().includes(name)));
    }, [rows, filters]);

    const columnDefs = useMemo(() => [
        {field: "start", headerName: "Início", flex: 1},
        {field: "leader", headerName: "Responsável", flex: 1},
        {
            field: "name", headerName: "Guarnição", flex: 1,
            cellRenderer: ({data, value}) => (
                <ActivityLink to={activityLinkUrl.garrison(data?.id)}>{value ?? ""}</ActivityLink>
            ),
        },
    ], []);

    const rowSelection = useMemo(() => ({
        mode: "multiRow",
        headerCheckbox: false,
        enableClickSelection: true,
        enableSelectionWithoutKeys: true,
    }), []);

    const selectionColumnDef = useMemo(() => ({
        headerName: "Sim",
    }), []);

    const preselect = ({api}) => {
        const ids = new Set((selectedIds ?? []).map(String));
        const nodes = [];
        api.forEachNode(node => {
            if (ids.has(String(node.data.id))) nodes.push(node);
        });
        if (nodes.length) api.setNodesSelected({nodes, newValue: true});
    };

    const selectMyGarrison = () => {
        if (!gridApi || !inspectorNameWar) return;
        const nodes = [];
        gridApi.forEachNode(node => {
            if (node.data.leader === inspectorNameWar) nodes.push(node);
        });
        if (nodes.length) gridApi.setNodesSelected({nodes, newValue: true});
    };

    const apply = () => setFilters(filterValues);

    const clear = () => {
        setFilterValues(initialFilters);
        setFilters(initialFilters);
    };

    const confirm = () => {
        const ids = (gridApi?.getSelectedRows() ?? []).map(row => row.id);
        onConfirm(ids);
        setOpen(false);
    };

    return (
        <Modal open={open} setOpen={setOpen} title="Guarnição" className="w-8/12">
            <Modal.Body className="max-h-[calc(100vh-180px)] overflow-auto">
                <div className="flex gap-4 mb-3">
                    <InputForm
                        classNameWrapper="w-1/3"
                        id="leader"
                        label="Responsável"
                        value={filterValues.leader}
                        onChange={({target: {value}}) => setFilterValues(prev => ({...prev, leader: value}))}
                    />
                    <InputForm
                        classNameWrapper="flex-1"
                        id="name"
                        label="Nome"
                        value={filterValues.name}
                        onChange={({target: {value}}) => setFilterValues(prev => ({...prev, name: value}))}
                    />
                    <div className="flex gap-2">
                        <Button onClick={clear} title="Limpar" target="primary-outline"><FaEraser/></Button>
                        <Button onClick={apply} title="Buscar"><FaSearch/></Button>
                        <Button target="primary-outline" onClick={selectMyGarrison}>Selecionar minhas guarnições</Button>
                    </div>
                </div>
                <div className="h-[calc(100vh-360px)] aggrid-row-pointer">
                    <AgGridReact
                        rowData={filteredRows}
                        columnDefs={columnDefs}
                        defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                        rowSelection={rowSelection}
                        selectionColumnDef={selectionColumnDef}
                        getRowId={({data}) => String(data.id)}
                        onGridReady={(params) => setGridApi(params.api)}
                        onRowDataUpdated={preselect}
                        animateRows={false}
                        suppressCellFocus={true}
                        gridOptions={{localeText: aggridUtils.localization()}}
                        theme={aggridUtils.generalTheme()}
                    />
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={confirm}>Adicionar</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ActivityGarrisonModal;
